import { useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardActionArea,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
	ButtonGroup,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Menu,
	MenuItem,
	Checkbox,
} from "@mui/material";
import { Build, Close, Delete, Download } from "@mui/icons-material";
import { usePostSamplingIssuesViewModel } from "./PostSamplingIssues.vm";
import { PortalTooltip } from "../../helpers";
import JobMap from "../../helpers/JobMap";
import { MissionMap } from "../../helpers/MissionMap";
import type { Sample, Core, MissionCSV } from "@rogoag/shared-ts";

const PANEL_HEIGHT = "calc(100vh - 140px)";

export const PostSamplingIssues: React.FC = () => {
	const vm = usePostSamplingIssuesViewModel();

	if (!vm.isDeveloper) {
		return (
			<Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: "md", margin: "auto" }}>
				<Alert severity="error">You do not have permission to access this page.</Alert>
			</Box>
		);
	}

	return (
		<Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: "xl", margin: "auto" }}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
				<Typography variant="h5">Post-Sampling Issues</Typography>
				<Button
					variant="contained"
					onClick={vm.fixAll}
					disabled={vm.loading || vm.fixingAll || vm.jobs.length === 0}
					startIcon={vm.fixingAll ? <CircularProgress size={20} color="inherit" /> : undefined}
				>
					{vm.fixingAll
						? `Fixing All... ${vm.countdown !== null ? `(${vm.countdown}s)` : ""}`
						: `Fix All (${vm.jobs.length})`}
				</Button>
			</Stack>

			{vm.jobs.length > 0 && (
				<Alert severity="info" sx={{ mb: 2 }}>
					These jobs are stuck in post-sampling processing.
				</Alert>
			)}

			{vm.error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{vm.error}
				</Alert>
			)}

			{vm.loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress />
				</Box>
			) : vm.jobs.length === 0 ? (
				<Alert severity="success">No post-sampling issues found.</Alert>
			) : (
				<Box sx={{ display: "flex", gap: 2 }}>
					{/* Left panel — compact job cards */}
					<Box
						sx={{
							width: 350,
							minWidth: 350,
							height: PANEL_HEIGHT,
							overflowY: "auto",
						}}
					>
						<Stack spacing={2}>
							{Object.entries(
								vm.jobs.reduce<Record<string, typeof vm.jobs>>((groups, job) => {
									const company = job.companyName || "Unknown Company";
									(groups[company] ??= []).push(job);
									return groups;
								}, {}),
							)
								.sort(([a], [b]) => a.localeCompare(b))
								.map(([company, jobs]) => (
									<Box key={company}>
										<Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ px: 1 }}>
											{company} ({jobs.length})
										</Typography>
										<Stack spacing={1} sx={{ mt: 0.5 }}>
											{jobs.map((job) => {
												const isSelected = vm.selectedJob?.id === job.id;
												return (
													<Card
														key={job.id}
														variant="outlined"
														sx={{
															borderColor: isSelected ? "primary.main" : undefined,
															borderWidth: isSelected ? 2 : 1,
															bgcolor: isSelected ? "action.selected" : undefined,
														}}
													>
														<CardActionArea onClick={() => vm.selectJob(job)}>
															<CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
																<Typography variant="body2" fontWeight="bold" noWrap>
																	{job.fieldName || "Unnamed Field"}
																</Typography>
																<Typography variant="caption" color="text.secondary" noWrap component="div">
																	{job.growerName || "—"}
																</Typography>
																<Typography variant="caption" color="text.secondary" noWrap component="div">
																	{job.status || "—"}
																</Typography>
															</CardContent>
														</CardActionArea>
													</Card>
												);
											})}
										</Stack>
									</Box>
								))}
						</Stack>
					</Box>

					{/* Right panel — detail pane */}
					<Box
						sx={{
							flex: 1,
							height: PANEL_HEIGHT,
							overflowY: "auto",
						}}
					>
						{!vm.selectedJob ? (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									height: "100%",
								}}
							>
								<Typography color="text.secondary">Select a job to view details</Typography>
							</Box>
						) : (
							<JobDetailPane vm={vm} />
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};

type VM = ReturnType<typeof usePostSamplingIssuesViewModel>;

function JobDetailPane({ vm }: { vm: VM }) {
	const job = vm.selectedJob!;
	const isFixing = vm.fixingIds.has(job.id);
	const availableAttachments = vm.getAvailableAttachments(job);
	const [detailModal, setDetailModal] = useState<"samples" | "cores" | null>(null);

	return (
		<Stack spacing={2}>
			{/* Header */}
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
				<Box>
					<Typography variant="h5">{job.fieldName || "Unnamed Field"}</Typography>
					<Typography variant="body2" color="text.secondary">
						{[job.growerName, job.farmName, job.companyName].filter(Boolean).join(" · ")}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{job.id}
					</Typography>
				</Box>
				{isFixing ? (
					<CircularProgress size={28} />
				) : (
					<PortalTooltip title="Reprocess">
						<IconButton onClick={() => vm.fixOne(job.id)} disabled={vm.fixingAll} color="primary">
							<Build />
						</IconButton>
					</PortalTooltip>
				)}
			</Box>

			<Divider />

			{/* Info grid — 3-column layout */}
			<Box
				display="grid"
				gridTemplateColumns="repeat(3, auto 1fr)"
				columnGap={2}
				rowGap={0.5}
				sx={{ "& > :nth-of-type(4n+3)": { pl: 2 }, "& > :nth-of-type(4n+5)": { pl: 2 } }}
			>
				{/* Row 1: Location */}
				<InfoRow label="Company" value={job.companyName} />
				<InfoRow label="Grower" value={job.growerName} />
				<InfoRow label="Farm" value={job.farmName} />

				{/* Row 2: Field info */}
				<InfoRow label="Field" value={job.fieldName} />
				<InfoRow label="Status" value={job.status} />
				<InfoRow label="Priority" value={job.fieldPriority} />

				{/* Row 3: Dates */}
				<InfoRow label="Date Created" value={new Date(job.dateCreated).toLocaleDateString()} />
				<InfoRow
					label="Sample Date"
					value={job.dateSampled ? new Date(job.dateSampled).toLocaleDateString() : undefined}
				/>
				<InfoRow
					label="Date Shipped"
					value={job.dateShipped ? new Date(job.dateShipped).toLocaleDateString() : undefined}
				/>

				{/* Row 4: Lab & sampling */}
				<InfoRow label="Lab" value={job.labName} />
				<InfoRow label="Test Package" value={job.testPackage} />
				<InfoRow label="Season" value={job.season} />

				{/* Row 5: Metrics */}
				<InfoRow label="Depth" value={job.depth ? String(job.depth) : undefined} />
				<InfoRow label="Density" value={job.density ? String(job.density) : undefined} />
				<InfoRow label="Boundary Acres" value={job.boundaryAcres ? String(job.boundaryAcres) : undefined} />

				{/* Row 6: Submission */}
				<InfoRow label="# Samples" value={job.numberOfSamples ? String(job.numberOfSamples) : undefined} />
				<InfoRow label="Submitter" value={job.submitterName} />
				<InfoRow label="Submitter Email" value={job.submitterEmail} />

				{/* Row 7: Lab results & extras */}
				<InfoRow
					label="Lab Results Sent"
					value={job.dateLabResultsSent ? new Date(job.dateLabResultsSent).toLocaleDateString() : undefined}
				/>
				{job.eventId ? (
					<InfoRow label="Event ID" value={job.eventId} />
				) : (
					<>
						<Box />
						<Box />
					</>
				)}
				{job.jobFlags.length > 0 ? (
					<InfoRow label="Flags" value={job.jobFlags.join(", ")} />
				) : (
					<>
						<Box />
						<Box />
					</>
				)}
			</Box>

			{/* Submission notes — full width, shown only if present */}
			{job.submissionNotes && (
				<Box>
					<Typography variant="body2" color="text.secondary">
						Submission Notes:
					</Typography>
					<Typography variant="body2">{job.submissionNotes}</Typography>
				</Box>
			)}

			{/* Error notes */}
			{job.errorNotes.filter((e) => e.source === "Exit Interview Lambda").length > 0 && (
				<Box>
					<Typography variant="caption" color="error" fontWeight="bold">
						Errors:
					</Typography>
					<Box component="ul" sx={{ m: 0, pl: 2 }}>
						{job.errorNotes
							.filter((e) => e.source === "Exit Interview Lambda")
							.map((error, i) => (
								<Typography key={i} component="li" variant="caption" color="error">
									{error.note}
								</Typography>
							))}
					</Box>
				</Box>
			)}

			{/* Attachments */}
			{availableAttachments.length > 0 && (
				<Box>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Attachments
					</Typography>
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
						{availableAttachments.map((att) => (
							<Chip
								key={att.key}
								label={att.label}
								icon={<Download />}
								variant="outlined"
								clickable
								onClick={() => vm.downloadAttachment(att, job.id)}
							/>
						))}
					</Box>
				</Box>
			)}

			{/* Exe Mission details */}
			{vm.exeMission && (
				<Box>
					<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
						Executed Mission
					</Typography>
					<Box
						display="grid"
						gridTemplateColumns="repeat(3, auto 1fr)"
						columnGap={2}
						rowGap={0.5}
						sx={{ "& > :nth-of-type(4n+3)": { pl: 2 }, "& > :nth-of-type(4n+5)": { pl: 2 } }}
					>
						<InfoRow label="Type" value={vm.exeMission.type} />
						<InfoRow label="Density" value={vm.exeMission.density ? String(vm.exeMission.density) : undefined} />
						<InfoRow label="Field Name" value={vm.exeMission.fieldName} />

						<InfoRow label="Samples" value={String(vm.exeMission.centroids.length)} />
						<InfoRow label="Cores" value={String(vm.exeMission.cores.length)} />
						<InfoRow label="Zones" value={String(vm.exeMission.sampleZones.length)} />

						<InfoRow label="Rogo Version" value={vm.exeMission.extendedData.rogo_version} />
						<InfoRow label="Event ID" value={vm.exeMission.extendedData.event_id} />
						<InfoRow label="Lab" value={vm.exeMission.extendedData.lab_name} />

						<InfoRow label="Client" value={vm.exeMission.extendedData.client} />
						<InfoRow label="Grower" value={vm.exeMission.extendedData.grower} />
						<InfoRow label="Farm" value={vm.exeMission.extendedData.farm} />

						<InfoRow label="Lab Submittal ID" value={vm.exeMission.extendedData.lab_submittal_id} />
						<InfoRow label="Sample Order Type" value={vm.exeMission.extendedData.sample_order_type} />
						<InfoRow label="Last Modified" value={vm.exeMission.extendedData.last_modified} />
					</Box>
					<Stack direction="row" spacing={1} sx={{ mt: 1 }}>
						<Button size="small" variant="outlined" onClick={() => setDetailModal("samples")}>
							Sample Details ({vm.exeMission.centroids.length})
						</Button>
						<Button size="small" variant="outlined" onClick={() => setDetailModal("cores")}>
							Core Details ({vm.exeMission.cores.length})
						</Button>
						<Button
							size="small"
							variant="outlined"
							onClick={() => vm.setCsvDryRunModalOpen(true)}
							disabled={!vm.csvDryRunData}
						>
							CSV Dry-Run
						</Button>
					</Stack>
					{vm.exeMission.extendedData.in_field_notes_ops && (
						<Typography variant="body2" sx={{ mt: 0.5 }}>
							<Typography component="span" variant="body2" color="text.secondary">
								In-Field Notes:{" "}
							</Typography>
							{vm.exeMission.extendedData.in_field_notes_ops}
						</Typography>
					)}
					{vm.exeMission.extendedData.boundary_change_notes && (
						<Typography variant="body2" sx={{ mt: 0.5 }}>
							<Typography component="span" variant="body2" color="text.secondary">
								Boundary Change:{" "}
							</Typography>
							{vm.exeMission.extendedData.boundary_change_type} — {vm.exeMission.extendedData.boundary_change_notes}
						</Typography>
					)}
				</Box>
			)}

			<Divider />

			{/* Map toggle */}
			<Box>
				<ButtonGroup size="small" sx={{ mb: 1 }}>
					<Button variant={vm.mapTab === "geodata" ? "contained" : "outlined"} onClick={() => vm.setMapTab("geodata")}>
						Customer GeoData
					</Button>
					<Button variant={vm.mapTab === "exe" ? "contained" : "outlined"} onClick={() => vm.setMapTab("exe")}>
						Exe Mission
					</Button>
					<Button variant={vm.mapTab === "int" ? "contained" : "outlined"} onClick={() => vm.setMapTab("int")}>
						Int Mission
					</Button>
				</ButtonGroup>

				{vm.mapTab === "geodata" &&
					(vm.mapLoading ? (
						<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
							<CircularProgress />
						</Box>
					) : (
						<JobMap job={job} height={400} colorMode="layer-type" showEmptyState />
					))}

				{vm.mapTab === "exe" &&
					(vm.missionsLoading ? (
						<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
							<CircularProgress />
						</Box>
					) : vm.exeMission ? (
						<MissionMap mission={vm.exeMission} height={400} />
					) : (
						<Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
							No executed mission available.
						</Typography>
					))}

				{vm.mapTab === "int" &&
					(vm.missionsLoading ? (
						<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
							<CircularProgress />
						</Box>
					) : vm.intMission ? (
						<MissionMap mission={vm.intMission} height={400} />
					) : (
						<Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
							No intended mission available.
						</Typography>
					))}
			</Box>

			{vm.exeMission && (
				<>
					<SampleDetailsDialog
						open={detailModal === "samples"}
						onClose={() => setDetailModal(null)}
						samples={vm.exeMission.centroids}
					/>
					<CoreDetailsDialog
						open={detailModal === "cores"}
						onClose={() => setDetailModal(null)}
						cores={vm.exeMission.cores}
						onFudge={(coreId) => vm.fudgeCore(vm.selectedJob!.id, coreId)}
						onDelete={(coreId) => vm.deleteCore(vm.selectedJob!.id, coreId)}
						onDeleteSelected={(coreIds) => vm.deleteCores(vm.selectedJob!.id, coreIds)}
						fudgingCoreId={vm.fudgingCoreId}
					/>
				</>
			)}
			{vm.csvDryRunData && (
				<CsvDryRunDialog
					open={vm.csvDryRunModalOpen}
					onClose={() => vm.setCsvDryRunModalOpen(false)}
					csvs={vm.csvDryRunData}
					onDownload={vm.downloadCsvDryRun}
				/>
			)}
		</Stack>
	);
}

const SAMPLE_TYPE_LABELS: Record<number, string> = {
	0: "Regular",
	1: "ISNT 1",
	2: "ISNT 2",
	3: "Cyst",
};

function formatTimestamp(ts: string | number | undefined): string {
	if (!ts) return "—";
	let ms: number;
	if (typeof ts === "string") {
		ms = new Date(ts).getTime();
	} else if (ts > 1e15) {
		ms = ts / 1000;
	} else if (ts > 1e12) {
		ms = ts;
	} else {
		ms = ts * 1000;
	}
	// Catch bogus dates from upstream (e.g. microseconds parsed as ms → year 58167)
	if (ms > 1e15) ms = ms / 1000;
	const date = new Date(ms);
	if (isNaN(date.getTime()) || date.getTime() === 0) return "—";
	return date.toLocaleString();
}

function SampleDetailsDialog({ open, onClose, samples }: { open: boolean; onClose: () => void; samples: Sample[] }) {
	return (
		<Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper" disableScrollLock>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				Sample Details ({samples.length})
				<IconButton onClick={onClose} size="small">
					<Close />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				{samples.length === 0 ? (
					<Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
						No samples found.
					</Typography>
				) : (
					<TableContainer>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>ID</TableCell>
									<TableCell>Sample ID</TableCell>
									<TableCell>Original Sample ID</TableCell>
									<TableCell>Bag ID</TableCell>
									<TableCell>Order</TableCell>
									<TableCell>Type</TableCell>
									<TableCell>Start Depth</TableCell>
									<TableCell>End Depth</TableCell>
									<TableCell>Core Length</TableCell>
									<TableCell># Cores</TableCell>
									<TableCell>Lat</TableCell>
									<TableCell>Lng</TableCell>
									<TableCell>Sampled At</TableCell>
									<TableCell>Scanned At</TableCell>
									<TableCell>Pulled At</TableCell>
									<TableCell>Box UID</TableCell>
									<TableCell>External Ref ID</TableCell>
									<TableCell>Change</TableCell>
									<TableCell>Operator</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{samples.map((sample) => (
									<TableRow key={sample.id}>
										<TableCell>{sample.id}</TableCell>
										<TableCell>{sample.extendedData.sample_id || "—"}</TableCell>
										<TableCell>{sample.extendedData.original_sample_id || "—"}</TableCell>
										<TableCell>{sample.extendedData.bag_id || "—"}</TableCell>
										<TableCell>{sample.extendedData.order || "—"}</TableCell>
										<TableCell>
											{SAMPLE_TYPE_LABELS[sample.extendedData.sample_type as number] ??
												sample.extendedData.sample_type ??
												"—"}
										</TableCell>
										<TableCell>{sample.extendedData.start_depth || "—"}</TableCell>
										<TableCell>{sample.extendedData.end_depth || "—"}</TableCell>
										<TableCell>{sample.extendedData.core_length_cm || "—"}</TableCell>
										<TableCell>{sample.cores?.length ?? 0}</TableCell>
										<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
											{sample.coordinates?.[1] ? sample.coordinates[1].toFixed(6) : "—"}
										</TableCell>
										<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
											{sample.coordinates?.[0] ? sample.coordinates[0].toFixed(6) : "—"}
										</TableCell>
										<TableCell>{formatTimestamp(sample.extendedData.sampled_at)}</TableCell>
										<TableCell>{formatTimestamp(sample.extendedData.scanned_at)}</TableCell>
										<TableCell>{formatTimestamp(sample.extendedData.pulled_at)}</TableCell>
										<TableCell>{sample.extendedData.box_uid || "—"}</TableCell>
										<TableCell>{sample.extendedData.external_reference_id || "—"}</TableCell>
										<TableCell>
											{sample.extendedData.change_type && sample.extendedData.change_type !== "None"
												? `${sample.extendedData.change_type}${sample.extendedData.change_reason ? `: ${sample.extendedData.change_reason}` : ""}`
												: "—"}
										</TableCell>
										<TableCell>{sample.extendedData.operator || "—"}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</DialogContent>
		</Dialog>
	);
}

function CoreDetailsDialog({
	open,
	onClose,
	cores,
	onFudge,
	onDelete,
	onDeleteSelected,
	fudgingCoreId,
}: {
	open: boolean;
	onClose: () => void;
	cores: Core[];
	onFudge: (coreId: string) => void;
	onDelete: (coreId: string) => void;
	onDeleteSelected: (coreIds: string[]) => Promise<void>;
	fudgingCoreId: string | null;
}) {
	const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; coreId: string } | null>(null);
	const [selectedCores, setSelectedCores] = useState<Set<string>>(new Set());

	const allSelected = cores.length > 0 && selectedCores.size === cores.length;
	const someSelected = selectedCores.size > 0 && selectedCores.size < cores.length;

	const toggleAll = () => {
		if (allSelected) {
			setSelectedCores(new Set());
		} else {
			setSelectedCores(new Set(cores.map((c) => c.name)));
		}
	};

	const toggleCore = (name: string) => {
		setSelectedCores((prev) => {
			const next = new Set(prev);
			if (next.has(name)) next.delete(name);
			else next.add(name);
			return next;
		});
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper" disableScrollLock>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					Core Details ({cores.length})
					{selectedCores.size > 0 && (
						<Button
							size="small"
							variant="outlined"
							color="error"
							startIcon={<Delete />}
							disabled={fudgingCoreId === "bulk"}
							onClick={async () => {
								await onDeleteSelected(Array.from(selectedCores));
								setSelectedCores(new Set());
							}}
						>
							{fudgingCoreId === "bulk" ? <CircularProgress size={16} /> : `Delete Selected (${selectedCores.size})`}
						</Button>
					)}
				</Box>
				<IconButton onClick={onClose} size="small">
					<Close />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				{cores.length === 0 ? (
					<Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
						No cores found.
					</Typography>
				) : (
					<TableContainer>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell padding="checkbox">
										<Checkbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} size="small" />
									</TableCell>
									<TableCell padding="checkbox" />
									<TableCell>ID</TableCell>
									<TableCell>Sample ID</TableCell>
									<TableCell>Original Sample ID</TableCell>
									<TableCell>Bag ID</TableCell>
									<TableCell>Order</TableCell>
									<TableCell>Type</TableCell>
									<TableCell>Start Depth</TableCell>
									<TableCell>End Depth</TableCell>
									<TableCell>Core Length</TableCell>
									<TableCell>Planned Lat,Lon</TableCell>
									<TableCell>Pulled Lat,Lon</TableCell>
									<TableCell>Sampled At</TableCell>
									<TableCell>Scanned At</TableCell>
									<TableCell>Pulled At</TableCell>
									<TableCell>Box UID</TableCell>
									<TableCell>External Ref ID</TableCell>
									<TableCell>Change Type</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{cores.map((core, i) => (
									<TableRow key={`${core.id}-${i}`} selected={selectedCores.has(core.name)}>
										<TableCell padding="checkbox">
											<Checkbox
												checked={selectedCores.has(core.name)}
												onChange={() => toggleCore(core.name)}
												size="small"
											/>
										</TableCell>
										<TableCell padding="checkbox">
											{fudgingCoreId === core.name ? (
												<CircularProgress size={20} />
											) : (
												<IconButton
													size="small"
													onClick={(e) => setMenuAnchor({ el: e.currentTarget, coreId: core.name })}
												>
													<Build fontSize="small" />
												</IconButton>
											)}
										</TableCell>
										<TableCell>{core.id}</TableCell>
										<TableCell>{core.extendedData.sample_id || "—"}</TableCell>
										<TableCell>{core.extendedData.original_sample_id || "—"}</TableCell>
										<TableCell>{core.extendedData.bag_id || "—"}</TableCell>
										<TableCell>{core.extendedData.order || "—"}</TableCell>
										<TableCell>
											{SAMPLE_TYPE_LABELS[core.extendedData.sample_type as number] ??
												core.extendedData.sample_type ??
												"—"}
										</TableCell>
										<TableCell>{core.extendedData.start_depth || "—"}</TableCell>
										<TableCell>{core.extendedData.end_depth || "—"}</TableCell>
										<TableCell>{core.extendedData.core_length_cm || "—"}</TableCell>
										<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
											{core.plannedLatitude ? core.plannedLatitude.toFixed(6) : "0"},
											{core.plannedLongitude ? core.plannedLongitude.toFixed(6) : "0"}
										</TableCell>
										<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
											{core.pulledLatitude ? core.pulledLatitude.toFixed(6) : "0"},
											{core.pulledLongitude ? core.pulledLongitude.toFixed(6) : "0"}
										</TableCell>
										<TableCell>{formatTimestamp(core.extendedData.sampled_at)}</TableCell>
										<TableCell>{formatTimestamp(core.extendedData.scanned_at)}</TableCell>
										<TableCell>{formatTimestamp(core.extendedData.pulled_at)}</TableCell>
										<TableCell>{core.extendedData.box_uid || "—"}</TableCell>
										<TableCell>{core.extendedData.external_reference_id || "—"}</TableCell>
										<TableCell>{core.extendedData.change_type || "—"}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</DialogContent>
			<Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
				<MenuItem
					onClick={() => {
						if (menuAnchor) {
							onFudge(menuAnchor.coreId);
							setMenuAnchor(null);
						}
					}}
				>
					Fudge
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (menuAnchor) {
							onDelete(menuAnchor.coreId);
							setMenuAnchor(null);
						}
					}}
					sx={{ color: "error.main" }}
				>
					Delete
				</MenuItem>
			</Menu>
		</Dialog>
	);
}

function CsvDryRunDialog({
	open,
	onClose,
	csvs,
	onDownload,
}: {
	open: boolean;
	onClose: () => void;
	csvs: MissionCSV[];
	onDownload: () => void;
}) {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const csv = csvs[selectedIndex];
	const lines =
		csv
			?.toString()
			.split("\n")
			.filter((l) => l.length > 0) ?? [];
	const headers = lines[0]?.split(",") ?? [];
	const rows = lines.slice(1).map((line) => line.split(","));

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper" disableScrollLock>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					CSV Dry-Run
					{csvs.length > 1 && (
						<ButtonGroup size="small">
							{csvs.map((c, i) => (
								<Button
									key={c.filename}
									variant={i === selectedIndex ? "contained" : "outlined"}
									onClick={() => setSelectedIndex(i)}
								>
									{c.filename}
								</Button>
							))}
						</ButtonGroup>
					)}
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Button size="small" variant="outlined" startIcon={<Download />} onClick={onDownload}>
						Download
					</Button>
					<IconButton onClick={onClose} size="small">
						<Close />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent dividers>
				{csv && (
					<>
						<Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
							{csv.filename} — {rows.length} rows
						</Typography>
						<TableContainer>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										{headers.map((h, i) => (
											<TableCell key={i} sx={{ whiteSpace: "nowrap" }}>
												{h}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((row, ri) => (
										<TableRow key={ri}>
											{headers.map((_, ci) => (
												<TableCell key={ci} sx={{ whiteSpace: "nowrap" }}>
													{row[ci] || ""}
												</TableCell>
											))}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
	return (
		<>
			<Typography variant="body2" color="text.secondary">
				{label}:
			</Typography>
			<Typography variant="body2">{value || "—"}</Typography>
		</>
	);
}
